import { type IFuseOptions } from "fuse.js"
import { extractFields, matchesAnyExclude } from "./score"
import { type TieredMatch, tieredSearch } from "./tieredSearch"
import {
  DEFAULT_FIELD_WEIGHTS,
  type FieldWeights,
  type MatchTier,
  type SearchOptions,
  type SearchResult,
  type SearchableProduct,
} from "./types"

function buildFuseOptions<T extends SearchableProduct>(
  weights: FieldWeights,
  threshold: number
): IFuseOptions<T> {
  return {
    keys: [
      { name: "name", weight: weights.name },
      { name: "short_name", weight: weights.short_name },
      { name: "sku", weight: weights.sku },
      { name: "code", weight: weights.code },
      { name: "labels.name", weight: weights.labels },
    ],
    threshold,
    includeScore: true,
    minMatchCharLength: 2,
    ignoreLocation: true,
    useExtendedSearch: true,
  }
}

const TIER7_TO_5: Record<TieredMatch, Exclude<MatchTier, "none">> = {
  "exact-code": "exact",
  "exact-field": "exact",
  "code-prefix": "starts-with",
  "word-prefix": "starts-with",
  substring: "contains",
  "token-match": "contains",
  fuzzy: "fuzzy",
}

const TIER5_SCORE: Record<Exclude<MatchTier, "none" | "fuzzy">, number> = {
  exact: 1.0,
  "starts-with": 0.9,
  contains: 0.8,
}

/**
 * Rank candidate products against a free-text query and return results in the
 * legacy 5-tier shape (`exact` > `starts-with` > `contains` > `fuzzy`) that
 * the V2 AI tool consumes.
 *
 * Internally delegates to `tieredSearch` (the shared 7-tier core); the 7 tiers
 * are folded into 5 via {@link TIER7_TO_5}. Sleeping products and `exclude`
 * tokens are pre-filtered before ranking.
 *
 * `topN` truncates the matched set; if `includeNonMatching: true`, remaining
 * products are appended (matchType: "none", score: 0) AFTER the matched set
 * but BEFORE any topN cap is re-applied — i.e. topN bounds matches only.
 *
 * Note: the `boostExactOrder` option is retained for shape parity but is now a
 * no-op — `tieredSearch`'s per-word Fuse averaging delivers the same intent
 * (rewarding queries whose words appear together in the target) more robustly.
 */
export function rankCandidates<T extends SearchableProduct>(
  products: T[],
  query: string,
  options: SearchOptions = {}
): SearchResult<T>[] {
  const {
    threshold = 0.4,
    includeNonMatching = false,
    normalizeNumbers = true,
    includeSleeping = false,
    topN,
    exclude = [],
    fieldWeights,
  } = options

  const weights: FieldWeights = { ...DEFAULT_FIELD_WEIGHTS, ...fieldWeights }

  // Pre-filter: sleeping + excludes. The ranker doesn't see these rows at all.
  const candidates: T[] = []
  for (const p of products) {
    if (!includeSleeping && p.is_sleeping) continue
    if (exclude.length > 0 && matchesAnyExclude(extractFields(p), exclude)) {
      continue
    }
    candidates.push(p)
  }

  const trimmed = query.trim()
  if (!trimmed) {
    if (!includeNonMatching) return []
    const all = candidates.map<SearchResult<T>>((product) => ({
      item: product,
      score: 0,
      matchType: "none",
    }))
    return topN !== undefined ? all.slice(0, topN) : all
  }

  const ranked = tieredSearch(candidates, trimmed, {
    codeField: (p) => p.code,
    textFields: (p) => [
      p.name,
      p.short_name,
      p.sku,
      p.code,
      ...(p.labels ?? []).map((l) => l?.name ?? null),
    ],
    fuseOptions: buildFuseOptions<T>(weights, threshold),
    normalizeNumbers,
  })

  const matched: SearchResult<T>[] = ranked.map((r) => {
    const tier5 = TIER7_TO_5[r.tier]
    const score =
      tier5 === "fuzzy" ? 1 - (r.fuseScore ?? 0) : TIER5_SCORE[tier5]
    return { item: r.item, score, matchType: tier5 }
  })

  const truncated = topN !== undefined ? matched.slice(0, topN) : matched

  if (!includeNonMatching) return truncated

  const matchedIds = new Set(matched.map((m) => m.item.id))
  const tail: SearchResult<T>[] = []
  for (const product of candidates) {
    if (!matchedIds.has(product.id)) {
      tail.push({ item: product, score: 0, matchType: "none" })
    }
  }
  return truncated.concat(tail)
}
